import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  ShoppingCart, 
  Package, 
  Warehouse, 
  TrendingUp, 
  Headphones, 
  CheckCircle,
  ArrowRight 
} from 'lucide-react';

const Home = () => {
  const [stats, setStats] = useState({
    reviews: 0,
    revenue: 0,
    experience: 0,
    adSpend: 0
  });

  const statsRef = useRef(null);
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasAnimated) {
          animateStats();
          setHasAnimated(true);
        }
      },
      { threshold: 0.3 }
    );

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }

    return () => observer.disconnect();
  }, [hasAnimated]);

  const animateStats = () => {
    const duration = 2000;
    const steps = 60;
    const interval = duration / steps;

    let currentStep = 0;

    const timer = setInterval(() => {
      currentStep++;
      const progress = currentStep / steps;

      setStats({
        reviews: Math.floor(100 * progress),
        revenue: Math.floor(45 * progress),
        experience: Math.floor(13 * progress),
        adSpend: Math.floor(20 * progress)
      });

      if (currentStep >= steps) {
        clearInterval(timer);
        setStats({
          reviews: 100,
          revenue: 45,
          experience: 13,
          adSpend: 20
        });
      }
    }, interval);
  };

  const services = [
    {
      icon: <ShoppingCart className="w-10 h-10" />,
      title: 'Onboarding',
      description: 'We purchase products upfront and manage complete Amazon sales operations.'
    },
    {
      icon: <Package className="w-10 h-10" />,
      title: 'Receiving Shipment',
      description: 'Inventory inspection, labeling, prep, and Amazon compliance handling.'
    },
    {
      icon: <Warehouse className="w-10 h-10" />,
      title: 'Warehouse Distribution',
      description: 'Nationwide distribution to Amazon fulfillment centers for Prime eligibility.'
    },
    {
      icon: <TrendingUp className="w-10 h-10" />,
      title: 'Listing Optimization',
      description: 'AI-driven PPC campaign management with real-time bid optimization.'
    },
    {
      icon: <Headphones className="w-10 h-10" />,
      title: 'Customer Support',
      description: 'Active inventory monitoring and brand-focused issue resolution.'
    },
    {
      icon: <CheckCircle className="w-10 h-10" />,
      title: 'Quality Assurance',
      description: 'Rigorous quality checks ensuring brand standards and customer satisfaction.'
    }
  ];

  const platforms = [
    { name: 'Amazon', logo: 'https://upload.wikimedia.org/wikipedia/commons/a/a9/Amazon_logo.svg' },
    { name: 'Costco', logo: 'https://upload.wikimedia.org/wikipedia/commons/5/59/Costco_Wholesale_logo_2010-10-26.svg' },
    { name: 'Best Buy', logo: 'https://upload.wikimedia.org/wikipedia/commons/f/f5/Best_Buy_Logo.svg' }
  ];

  // Amazon product showcase data - Lane 1
  const amazonProductsLane1 = [
    'https://customer-assets.emergentagent.com/job_eyme-selling-partner/artifacts/2mzxf67e_1.webp',
    'https://customer-assets.emergentagent.com/job_eyme-selling-partner/artifacts/l62m8rjy_2.webp',
    'https://customer-assets.emergentagent.com/job_eyme-selling-partner/artifacts/36fp44j5_3.webp',
    'https://customer-assets.emergentagent.com/job_eyme-selling-partner/artifacts/9rlvzfav_4.webp',
    'https://customer-assets.emergentagent.com/job_eyme-selling-partner/artifacts/m4lo9ucf_5.webp'
  ];

  // Lane 2 - Second set of images
  const amazonProductsLane2 = [
    'https://customer-assets.emergentagent.com/job_eyme-selling-partner/artifacts/exfavjat_6.webp',
    'https://customer-assets.emergentagent.com/job_eyme-selling-partner/artifacts/tx4q11ly_7.webp',
    'https://customer-assets.emergentagent.com/job_eyme-selling-partner/artifacts/89jltu51_8.webp',
    'https://customer-assets.emergentagent.com/job_eyme-selling-partner/artifacts/faoisl26_9.webp',
    'https://customer-assets.emergentagent.com/job_eyme-selling-partner/artifacts/m5cufyp2_10.webp'
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section with Scrolling Products */}
      <section className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="container mx-auto px-6 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Column - Text Content */}
            <div className="text-left z-10">
              <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
                Elevate Your <br /><span className="text-[#FF9900]">Amazon</span> Presence
              </h1>
              <p className="text-xl text-gray-200 mb-8 leading-relaxed">
                We're your strategic selling partner. Every success starts with a clear goal—whether it's 
                scaling your online sales or unlocking growth through data. We deliver end-to-end solutions 
                that drive real results.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/about">
                  <Button className="bg-[#FF9900] hover:bg-[#e68a00] text-white px-8 py-4 text-lg font-semibold rounded-lg shadow-lg transform transition-all duration-300 hover:scale-105">
                    Learn More
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button className="bg-white hover:bg-gray-100 text-slate-900 px-8 py-4 text-lg font-semibold rounded-lg shadow-lg transform transition-all duration-300 hover:scale-105">
                    Partner With Us
                  </Button>
                </Link>
              </div>
            </div>

            {/* Right Column - Two-Lane Scrolling Products */}
            <div className="hidden lg:block relative h-[600px]">
              <div className="absolute inset-0 flex gap-6">
                {/* First Lane - Scrolling Up */}
                <div className="flex-1 overflow-hidden">
                  <div className="animate-scroll-up space-y-6">
                    {[...amazonProductsLane1, ...amazonProductsLane1, ...amazonProductsLane1].map((imageUrl, index) => (
                      <div 
                        key={`lane1-${index}`}
                        className="bg-white rounded-2xl shadow-2xl overflow-hidden transform hover:scale-105 hover:shadow-3xl transition-all duration-300"
                      >
                        <img 
                          src={imageUrl} 
                          alt={`Product ${index + 1}`}
                          className="w-full h-48 object-cover"
                          style={{ aspectRatio: '16/9' }}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Second Lane - Scrolling Down */}
                <div className="flex-1 overflow-hidden">
                  <div className="animate-scroll-down space-y-6">
                    {[...amazonProductsLane2, ...amazonProductsLane2, ...amazonProductsLane2].map((imageUrl, index) => (
                      <div 
                        key={`lane2-${index}`}
                        className="bg-white rounded-2xl shadow-2xl overflow-hidden transform hover:scale-105 hover:shadow-3xl transition-all duration-300"
                      >
                        <img 
                          src={imageUrl} 
                          alt={`Product ${index + 1}`}
                          className="w-full h-48 object-cover"
                          style={{ aspectRatio: '16/9' }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-8 text-center">
              Why Choose <span className="text-[#FF9900]">EYME LLC</span>?
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed text-center mb-12">
              At EYME LLC, we help brands grow with purpose. As a strategic Amazon selling partner, 
              we connect thousands of vendors to a global audience. From increasing product visibility 
              to leveraging data-driven strategies, our expert team is committed to driving measurable 
              results in the digital marketplace.
            </p>
          </div>

          {/* Statistics */}
          <div ref={statsRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mt-16">
            <div className="text-center p-8 bg-gradient-to-br from-slate-50 to-white rounded-xl shadow-md hover:shadow-xl transition-shadow duration-300">
              <div className="text-5xl font-bold text-[#FF9900] mb-2">{stats.reviews}%</div>
              <div className="text-gray-700 font-semibold text-lg">Positive Reviews</div>
            </div>
            <div className="text-center p-8 bg-gradient-to-br from-slate-50 to-white rounded-xl shadow-md hover:shadow-xl transition-shadow duration-300">
              <div className="text-5xl font-bold text-[#FF9900] mb-2">${stats.revenue}M+</div>
              <div className="text-gray-700 font-semibold text-lg">Annual Revenue</div>
            </div>
            <div className="text-center p-8 bg-gradient-to-br from-slate-50 to-white rounded-xl shadow-md hover:shadow-xl transition-shadow duration-300">
              <div className="text-5xl font-bold text-[#FF9900] mb-2">{stats.experience}+</div>
              <div className="text-gray-700 font-semibold text-lg">Years Experience</div>
            </div>
            <div className="text-center p-8 bg-gradient-to-br from-slate-50 to-white rounded-xl shadow-md hover:shadow-xl transition-shadow duration-300">
              <div className="text-5xl font-bold text-[#FF9900] mb-2">${stats.adSpend}K+</div>
              <div className="text-gray-700 font-semibold text-lg">Monthly Ad Spend</div>
            </div>
          </div>
        </div>
      </section>

      {/* What We Do Section */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4 text-center">
            What We <span className="text-[#FF9900]">Do</span>
          </h2>
          <p className="text-center text-gray-600 mb-16 text-lg">
            Comprehensive Amazon marketplace solutions tailored to your success
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <Card 
                key={index} 
                className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 bg-white"
              >
                <CardContent className="p-8">
                  <div className="text-[#FF9900] mb-4">
                    {service.icon}
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {service.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/contact">
              <Button className="bg-[#FF9900] hover:bg-[#e68a00] text-white px-8 py-6 text-lg font-semibold rounded-lg shadow-lg transform transition-all duration-300 hover:scale-105">
                Partner With Us <ArrowRight className="ml-2 inline" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Platforms Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-12 text-center">
            Selling on <span className="text-[#FF9900]">Major Platforms</span>
          </h2>
          <div className="flex flex-wrap justify-center items-center gap-12 md:gap-20">
            {platforms.map((platform, index) => (
              <div 
                key={index} 
                className="flex items-center justify-center p-6 bg-slate-50 rounded-xl shadow-md hover:shadow-lg transition-shadow duration-300"
              >
                <img 
                  src={platform.logo} 
                  alt={platform.name} 
                  className="h-16 w-auto object-contain grayscale hover:grayscale-0 transition-all duration-300"
                />
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
